import { Ionicons } from '@expo/vector-icons';
import { useEffect, useState } from 'react';
import { Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { agents, Report } from './data';

const TOUR = {
  background: '#F7F3EC',
  card: '#FFFCF7',
  cardSoft: '#EFE8DE',
  ink: '#211D19',
  muted: '#786F66',
  gold: '#B88B53',
  goldSoft: '#EFE0C9',
  line: '#E9E0D4',
  white: '#FFFFFF',
};

type TourProps = {
  visible: boolean;
  live: boolean;
  spotlight: Report;
  reduceMotion: boolean;
  onComplete: () => void;
};

export function OnboardingTour({ visible, live, spotlight, reduceMotion, onComplete }: TourProps) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (visible) setStep(0);
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = visible ? 'hidden' : previous;
    if (visible) window.scrollTo(0, 0);
    return () => { document.body.style.overflow = previous; };
  }, [visible]);
  useEffect(() => { if (Platform.OS === 'web' && typeof window !== 'undefined') window.scrollTo(0, 0); }, [step]);

  const next = () => step === 3 ? onComplete() : setStep((current) => current + 1);
  const title = [
    'Welcome to Blonde',
    'What needs you stays at the top',
    'Talk to an agent like a colleague',
    'Everyone has their own profile',
  ][step];
  const body = [
    'Every AI agent has a profile, posts a report each morning, and answers your messages. It works like a feed, so it feels familiar from day one.',
    'Decisions come first. Acknowledge, assign, or send a report. Every post shows its source and confidence.',
    'Open a profile and tap Message. Ask what changed, what needs attention, or what to do next. Each agent answers from its own data.',
    'Reviews, assignments, and messages carry each person’s name. Admins manage people, agents, and app settings.',
  ][step];

  return (
    <Modal visible={visible} animationType={reduceMotion ? 'none' : 'fade'} presentationStyle="fullScreen" onRequestClose={onComplete}>
      <SafeAreaView style={styles.safe}>
        <View style={styles.topBar}>
          <Pressable accessibilityRole="button" accessibilityLabel="Close tour" onPress={onComplete} style={styles.closeButton}>
            <Ionicons name="close" size={26} color={TOUR.ink} />
          </Pressable>
          <View style={styles.brandLockup}><Text style={styles.brandOverline}>THOMAS BLONDE</Text><Text style={styles.brand}>BLONDE</Text></View>
          <View style={styles.closeSpacer} />
        </View>

        <View style={styles.page}>
          <View style={[styles.copyBlock, step === 0 && styles.copyBlockWelcome]}>
            <Text style={styles.eyebrow}>{step === 0 ? 'WELCOME TO' : `QUICK TOUR · ${step + 1} OF 4`}</Text>
            {step === 0 ? (
              <Text style={styles.welcomeTitle}>Blonde</Text>
            ) : (
              <Text numberOfLines={3} adjustsFontSizeToFit minimumFontScale={0.75} style={styles.title}>{title}</Text>
            )}
            <View style={styles.goldRule} />
            <Text style={styles.body}>{body}</Text>
          </View>

          <View style={styles.visualArea}>
            {step === 0 && <AgentVisual />}
            {step === 1 && <PriorityVisual report={spotlight} live={live} />}
            {step === 2 && <MessageVisual />}
            {step === 3 && <PeopleVisual />}
          </View>

          <View style={styles.controls}>
            <View style={styles.dots}>{[0, 1, 2, 3].map((index) => <View key={index} style={[styles.dot, index === step && styles.dotActive]} />)}</View>
            {step < 3 ? (
              <View style={styles.buttonRow}>
                <Pressable accessibilityRole="button" onPress={next} style={[styles.button, styles.primaryButton, styles.halfButton]}><Text style={styles.primaryText}>Next</Text></Pressable>
                <Pressable accessibilityRole="button" onPress={onComplete} style={[styles.button, styles.secondaryButton]}><Text style={styles.secondaryText}>Skip</Text></Pressable>
              </View>
            ) : (
              <Pressable accessibilityRole="button" onPress={next} style={[styles.button, styles.primaryButton, styles.fullButton]}><Text style={styles.primaryText}>Get started</Text></Pressable>
            )}
          </View>
        </View>
      </SafeAreaView>
    </Modal>
  );
}

function AgentVisual() {
  return (
    <View style={styles.agentRow}>
      {agents.filter((agent) => agent.id !== 'owner').map((agent) => (
        <View key={agent.id} style={[styles.agentCircle, { backgroundColor: `${agent.color}3D` }]}>
          <Ionicons name={agent.icon as never} size={29} color={agent.color} />
        </View>
      ))}
    </View>
  );
}

function PriorityVisual({ report, live }: { report: Report; live: boolean }) {
  const agent = agents.find((item) => item.id === report.agentId) ?? agents[0];
  return (
    <View style={styles.reportPreview}>
      <View style={styles.previewTop}>
        <View style={[styles.previewAvatar, { backgroundColor: agent.color }]}><Text style={styles.previewAvatarText}>{agent.short}</Text></View>
        <View style={styles.previewIdentity}><Text style={styles.previewAgent}>{agent.name}</Text><Text style={styles.previewMeta}>{live ? 'LIVE REPORT' : 'EXAMPLE REPORT'}</Text></View>
        <View style={styles.priorityPill}><Text style={styles.priorityPillText}>{report.severity === 'critical' ? 'ACT TODAY' : report.severity.toUpperCase()}</Text></View>
      </View>
      <Text numberOfLines={2} style={styles.previewHeadline}>{report.headline}</Text>
      <Text numberOfLines={1} adjustsFontSizeToFit style={styles.previewValue}>{report.hero.value}</Text>
      <Text style={styles.previewLabel}>{report.hero.label}</Text>
      <View style={styles.previewChart}>{(report.series?.length ? report.series : [3, 5, 4, 6, 5, 7, 8]).map((value, index, values) => {
        const max = Math.max(...values); const height = 6 + (value / Math.max(max, 1)) * 25;
        return <View key={index} style={[styles.previewBar, { height }]} />;
      })}</View>
      <View style={styles.previewKpis}>{report.kpis.slice(0, 3).map((kpi) => <View key={kpi.label} style={styles.previewKpi}><Text numberOfLines={1} adjustsFontSizeToFit style={styles.previewKpiValue}>{kpi.value}</Text><Text numberOfLines={1} style={styles.previewKpiLabel}>{kpi.label}</Text></View>)}</View>
    </View>
  );
}

function MessageVisual() {
  return (
    <View style={styles.messageStack}>
      <View style={[styles.messageBubble, styles.messageBubbleUser]}><Text style={styles.messageUserText}>What changed since yesterday?</Text></View>
      <View style={[styles.messageBubble, styles.messageBubbleAgent]}><View style={styles.messageTyping}><View style={styles.typingDot} /><View style={styles.typingDot} /><View style={styles.typingDot} /></View><Text style={styles.messageAgentText}>Your agent checks its live data and answers here.</Text></View>
    </View>
  );
}

function PeopleVisual() {
  return (
    <View style={styles.peopleWrap}>
      <View style={styles.peopleRow}>
        <View style={[styles.personCircle, styles.personGold]}><Text style={styles.personInitial}>S</Text></View>
        <View style={[styles.personCircle, styles.personBlue]}><Text style={styles.personInitial}>A</Text></View>
      </View>
      <View style={styles.peopleLabels}><Text style={styles.peopleLabel}>YOU</Text><Text style={styles.peopleLabel}>ADMIN</Text></View>
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: TOUR.background },
  topBar: { height: 68, paddingHorizontal: 20, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: TOUR.line, backgroundColor: TOUR.card, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  closeButton: { width: 42, height: 42, borderRadius: 21, borderWidth: 1, borderColor: TOUR.line, backgroundColor: TOUR.background, alignItems: 'center', justifyContent: 'center' },
  closeSpacer: { width: 42, height: 42 },
  brandLockup: { alignItems: 'center' }, brandOverline: { color: TOUR.muted, fontSize: 7, letterSpacing: 1.8, fontWeight: '800' }, brand: { color: TOUR.ink, fontSize: 18, letterSpacing: 4.5, fontWeight: '500', marginTop: 2 },
  page: { flex: 1, paddingHorizontal: 28, paddingBottom: 20 },
  copyBlock: { paddingTop: 22 }, copyBlockWelcome: { paddingTop: 70 },
  eyebrow: { color: TOUR.muted, fontSize: 11, letterSpacing: 1.7, fontWeight: '800' },
  welcomeTitle: { color: TOUR.gold, fontSize: 48, lineHeight: 56, fontFamily: Platform.OS === 'ios' ? 'Times New Roman' : 'Georgia', marginTop: 8 },
  title: { color: TOUR.ink, fontSize: 32, lineHeight: 35, letterSpacing: -1, fontWeight: '800', marginTop: 11 },
  goldRule: { width: 55, height: 3, borderRadius: 2, backgroundColor: TOUR.gold, marginTop: 14 },
  body: { color: TOUR.muted, fontSize: 14, lineHeight: 20, marginTop: 16 },
  visualArea: { flex: 1, justifyContent: 'center', minHeight: 118 },
  agentRow: { flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 4 },
  agentCircle: { width: 62, height: 62, borderRadius: 31, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: TOUR.line },
  reportPreview: { alignSelf: 'center', width: '76%', borderRadius: 18, backgroundColor: TOUR.card, padding: 11, borderWidth: 1, borderColor: TOUR.line, shadowColor: '#5B4935', shadowOpacity: 0.09, shadowRadius: 22 },
  previewTop: { flexDirection: 'row', alignItems: 'center' }, previewAvatar: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center' }, previewAvatarText: { color: TOUR.white, fontSize: 9, fontWeight: '900' }, previewIdentity: { flex: 1, marginLeft: 8 }, previewAgent: { color: TOUR.ink, fontSize: 10, fontWeight: '800' }, previewMeta: { color: TOUR.muted, fontSize: 6, letterSpacing: 0.8, marginTop: 2 },
  priorityPill: { borderRadius: 999, backgroundColor: TOUR.goldSoft, paddingHorizontal: 7, paddingVertical: 5 }, priorityPillText: { color: TOUR.gold, fontSize: 6, letterSpacing: 0.7, fontWeight: '900' },
  previewHeadline: { color: TOUR.muted, fontSize: 8, lineHeight: 11, marginTop: 7 }, previewValue: { color: TOUR.ink, fontSize: 27, lineHeight: 31, fontWeight: '800', letterSpacing: -1, marginTop: 5 }, previewLabel: { color: TOUR.muted, fontSize: 6, textTransform: 'uppercase', letterSpacing: 0.8 },
  previewChart: { height: 33, marginTop: 6, flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between' }, previewBar: { width: 7, borderRadius: 4, backgroundColor: TOUR.gold, opacity: 0.78 },
  previewKpis: { flexDirection: 'row', gap: 5, marginTop: 7 }, previewKpi: { flex: 1, minHeight: 34, borderRadius: 8, padding: 5, justifyContent: 'center', backgroundColor: '#F5F0E8' }, previewKpiValue: { color: TOUR.ink, fontSize: 10, fontWeight: '900' }, previewKpiLabel: { color: TOUR.muted, fontSize: 5, textTransform: 'uppercase', marginTop: 2 },
  messageStack: { gap: 10, paddingHorizontal: 2 }, messageBubble: { maxWidth: '88%', paddingHorizontal: 17, paddingVertical: 13, borderRadius: 18 }, messageBubbleUser: { alignSelf: 'flex-end', backgroundColor: TOUR.goldSoft, borderBottomRightRadius: 5 }, messageBubbleAgent: { alignSelf: 'flex-start', backgroundColor: TOUR.card, borderWidth: 1, borderColor: TOUR.line, borderBottomLeftRadius: 5 }, messageUserText: { color: TOUR.ink, fontSize: 16, fontWeight: '600' }, messageAgentText: { color: TOUR.muted, fontSize: 13, lineHeight: 18, marginTop: 8 }, messageTyping: { flexDirection: 'row', gap: 4 }, typingDot: { width: 5, height: 5, borderRadius: 3, backgroundColor: TOUR.gold },
  peopleWrap: { alignSelf: 'flex-start' }, peopleRow: { flexDirection: 'row', gap: 14 }, personCircle: { width: 68, height: 68, borderRadius: 34, alignItems: 'center', justifyContent: 'center' }, personGold: { backgroundColor: '#DDB96E' }, personBlue: { backgroundColor: '#5FAFB0' }, personInitial: { color: TOUR.ink, fontSize: 27, fontWeight: '900' }, peopleLabels: { flexDirection: 'row', gap: 14, marginTop: 7 }, peopleLabel: { width: 68, textAlign: 'center', color: TOUR.muted, fontSize: 8, letterSpacing: 1.1, fontWeight: '800' },
  controls: { paddingTop: 8 }, dots: { height: 24, flexDirection: 'row', alignItems: 'center', gap: 7 }, dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#D5CEC4' }, dotActive: { backgroundColor: TOUR.gold },
  buttonRow: { flexDirection: 'row', gap: 10, marginTop: 8 }, button: { minHeight: 54, borderRadius: 27, alignItems: 'center', justifyContent: 'center' }, primaryButton: { backgroundColor: TOUR.goldSoft, shadowColor: TOUR.gold, shadowOpacity: 0.16, shadowRadius: 16, shadowOffset: { width: 0, height: 4 } }, halfButton: { flex: 1 }, secondaryButton: { flex: 1, backgroundColor: TOUR.cardSoft }, fullButton: { width: '100%', marginTop: 8 }, primaryText: { color: TOUR.ink, fontSize: 16, fontWeight: '900' }, secondaryText: { color: TOUR.ink, fontSize: 16, fontWeight: '800' },
});
